import { Dialog, DialogContent } from "@mui/material";
import PropTypes from "prop-types";
import { useCallback, useEffect, useState } from "react";
import { RetailerConditionWizard } from "../retailerOutcome";
import { StoreConditionWizard } from "../storeOutcome";
import ConditionTypeSelector from "./ConditionTypeSelector";
import SurveyListTable from "./SurveyListTable";
import SurveyQuestionSelector from "./SurveyQuestionSelector";

const STEPS = {
  CONDITION_TYPE: "CONDITION_TYPE",
  SURVEY_LIST: "SURVEY_LIST",
  QUESTION_SELECT: "QUESTION_SELECT",
  STORE: "STORE",
  RETAILER: "RETAILER",
};

// editData without an explicit `type` is treated as a legacy survey edit payload.
const resolveEditType = (editData) => (editData ? editData.type || "survey" : null);

export default function AddConditionModal({
  open,
  onClose,
  onConditionAdd,
  editData,
  enabledTypes,
}) {
  const editType = resolveEditType(editData);

  const [step, setStep] = useState(STEPS.CONDITION_TYPE);
  const [selectedType, setSelectedType] = useState(null);
  const [selectedSurvey, setSelectedSurvey] = useState(null);

  const resetState = useCallback(() => {
    setStep(STEPS.CONDITION_TYPE);
    setSelectedType(null);
    setSelectedSurvey(null);
  }, []);

  // Initialize edit mode when editData is provided — jump straight to the relevant
  // sub-flow (survey question selector, store wizard, or retailer wizard) instead of the picker.
  useEffect(() => {
    if (editData && open) {
      if (editType === "storeGroup") {
        setSelectedType("storeGroup");
        setStep(STEPS.STORE);
      } else if (editType === "retailer") {
        setSelectedType("retailer");
        setStep(STEPS.RETAILER);
      } else {
        setSelectedType("survey");
        setSelectedSurvey(editData.survey);
        setStep(STEPS.QUESTION_SELECT);
      }
    } else if (!open) {
      resetState();
    }
  }, [editData, open, resetState, editType]);

  const handleClose = useCallback(() => {
    resetState();
    onClose();
  }, [resetState, onClose]);

  const handleContinue = useCallback(() => {
    if (selectedType === "survey") {
      setStep(STEPS.SURVEY_LIST);
    } else if (selectedType === "storeGroup") {
      setStep(STEPS.STORE);
    } else if (selectedType === "retailer") {
      setStep(STEPS.RETAILER);
    }
  }, [selectedType]);

  const handleSurveySelect = useCallback((survey) => {
    setSelectedSurvey(survey);
    setStep(STEPS.QUESTION_SELECT);
  }, []);

  const handleBack = useCallback(() => {
    if (step === STEPS.SURVEY_LIST) {
      setStep(STEPS.CONDITION_TYPE);
    } else if (step === STEPS.QUESTION_SELECT) {
      setStep(STEPS.SURVEY_LIST);
    }
  }, [step]);

  const handleConditionAdd = useCallback(
    (ruleData) => {
      onConditionAdd(ruleData, "survey");
      handleClose();
    },
    [onConditionAdd, handleClose]
  );

  const handleStoreConditionAdd = useCallback(
    (ruleData) => {
      onConditionAdd(ruleData, "storeGroup");
      handleClose();
    },
    [onConditionAdd, handleClose]
  );

  const handleRetailerConditionAdd = useCallback(
    (ruleData) => {
      onConditionAdd(ruleData, "retailer");
      handleClose();
    },
    [onConditionAdd, handleClose]
  );

  const isConditionListing = step === STEPS.STORE || step === STEPS.RETAILER;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth={step === STEPS.CONDITION_TYPE ? "md" : "lg"}
      PaperProps={{
        className: `condition-selection-dialog${
          isConditionListing ? " condition-selection-dialog--listing" : ""
        }`,
      }}
      data-testid="add-condition-modal"
    >
      <DialogContent
        data-testid="add-condition-modal-content"
        className={isConditionListing ? "condition-selection-dialog__content" : ""}
      >
        {step === STEPS.CONDITION_TYPE && (
          <ConditionTypeSelector
            selectedType={selectedType}
            onTypeSelect={setSelectedType}
            onContinue={handleContinue}
            onCancel={handleClose}
            enabledTypes={enabledTypes}
          />
        )}
        {step === STEPS.SURVEY_LIST && (
          <SurveyListTable
            onSurveySelect={handleSurveySelect}
            onBack={handleBack}
            selectedSurveyId={selectedSurvey?.surveyId}
          />
        )}
        {step === STEPS.QUESTION_SELECT && selectedSurvey && (
          <SurveyQuestionSelector
            survey={selectedSurvey}
            onConditionAdd={handleConditionAdd}
            onCancel={handleClose}
            onBack={handleBack}
            initialSelections={editData?.selections}
            initialLogicOperator={editData?.logic_operator}
          />
        )}
        {step === STEPS.STORE && (
          <StoreConditionWizard
            editData={editType === "storeGroup" ? editData : null}
            onConditionAdd={handleStoreConditionAdd}
            onCancel={handleClose}
            onBackToTypeSelect={
              editType === "storeGroup"
                ? undefined
                : () => setStep(STEPS.CONDITION_TYPE)
            }
          />
        )}
        {step === STEPS.RETAILER && (
          <RetailerConditionWizard
            editData={editType === "retailer" ? editData?.retailers : null}
            onConditionAdd={handleRetailerConditionAdd}
            onCancel={handleClose}
            onBackToTypeSelect={
              editType === "retailer"
                ? undefined
                : () => setStep(STEPS.CONDITION_TYPE)
            }
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

AddConditionModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  // Called with (ruleData, type) where type is "survey" | "storeGroup" | "retailer".
  onConditionAdd: PropTypes.func.isRequired,
  editData: PropTypes.shape({
    type: PropTypes.string,
    survey: PropTypes.object,
    selections: PropTypes.array,
    logic_operator: PropTypes.string,
    category: PropTypes.string,
    storeIds: PropTypes.arrayOf(PropTypes.string),
    selectionType: PropTypes.string,
  }),
  // Which condition-type cards the picker enables; forwarded to ConditionTypeSelector.
  enabledTypes: PropTypes.objectOf(PropTypes.bool),
};

AddConditionModal.defaultProps = {
  editData: null,
  enabledTypes: undefined,
};
